/**
 * The app's fixed routes, in one place, shared by the app and `server.js`
 * (plain JS for that reason, typed by `routes.d.ts`).
 *
 * Internal system routes, the ones a person reaches from an e-mail, a QR code
 * or another system rather than from the menu, live under
 * `/_/{module}/{action}`. Navigation pages (`/pricing`, `/account`,
 * `/admin/...`) keep their plain paths. Everything is in English.
 */
export const ROUTES = {
  /** `/_/certificates/verify/{code}`: public verification (F-42). */
  certificatesVerify: '/_/certificates/verify/',
  /** `/_/checkout/payment/{purchaseId}`: a checkout order's confirmation. */
  checkoutPayment: '/_/checkout/payment/',
  /** `/_/products/{productId}`: the product page, or its renewal checkout (F-74 e-mails). */
  products: '/_/products/',
  /** `/_/waitlist/{productId}`: where a waitlist offer is accepted (F-74 e-mail). */
  waitlist: '/_/waitlist/',
  /** `/_/downloads/mine`: "My downloads", inside "My purchases" (F-75). */
  myDownloads: '/_/downloads/mine',
  /** `/_/parcels/mine`: "My parcels", the page the arrival e-mail links to (F-81). */
  myParcels: '/_/parcels/mine',
  /** `/_/day-passes/mine`: "Meus passes", the page the pass e-mails link to (F-249). */
  myDayPasses: '/_/day-passes/mine',
  /** `/_/tickets/mine`: "Meus ingressos", pinned in the menu (the `?tab=tickets` of Minha conta until 2026-11-05). */
  myTickets: '/_/tickets/mine',
  /** `/_/purchases/mine`: "Minhas compras", pinned in the menu (a card of Faturamento until 2026-11-05). */
  myPurchases: '/_/purchases/mine',
  /** `/_/credits/mine`: "Créditos", pinned in the menu (the `?tab=credits` of Minha conta until 2026-11-05). */
  myCredits: '/_/credits/mine',
  /** `/_/support/tickets/{ticketId}`: the link of the Support e-mails (F-82). */
  supportTicket: '/_/support/tickets/',
  /** `/_/downsell/offer/{token}`: the last-chance offer e-mail link (F-39). */
  downsellOffer: '/_/downsell/offer/',
  /** `/_/invite/decline/{token}`: "thanks, but I do not want to join", from the import invite e-mail (F-85). */
  inviteDecline: '/_/invite/decline/',
  /** `/_/auth/set-password?id=…&token=…`: the invite link (PROF-3). */
  authSetPassword: '/_/auth/set-password',
  /**
   * `/support-entry#token=…`: the SuperAdmin's "Entrar" link (F-31 F2), opened on
   * the community's own domain. The API builds this exact path; the token rides
   * in the fragment so it never reaches a server log.
   */
  supportEntry: '/support-entry',
  /** `/_/surveys/{id}`: a member answers a survey of their community (F-103). */
  surveyAnswer: '/_/surveys/',
  /** `/_/recognitions/{id}`: one recognition, the link of the "you were recognized" e-mail (F-53). Needs a session. */
  recognition: '/_/recognitions/',
  /** `/_/feedbacks/{id}`: one feedback, the link of the "you got feedback" e-mail (F-49). Author and recipient only. */
  feedback: '/_/feedbacks/',
  /** `/_/feedbacks/requests/{id}`: one feedback request, the link of the request and answer e-mails (F-49). */
  feedbackRequest: '/_/feedbacks/requests/',
  /** `/_/development-plans/{id}`: one PDI, the link of the PDI e-mails (F-52). Who reads it is the API's. */
  developmentPlan: '/_/development-plans/',
  timeTracking: '/_/time-tracking/',
  /** `/_/chat/{conversationId}`: opens the chat panel on one conversation, the link of the unread-message e-mail (F-108). Participants only. */
  chat: '/_/chat/',
  /** `/_/notifications`: opens the notifications bell (F-186 phase 2: a team notice's push and the "Você tem N novidades" summary). */
  notifications: '/_/notifications',
  /** `/s/{publicToken}`: a survey's public link, no login (F-103 §7a). Short on purpose: it is shared by hand. */
  publicSurvey: '/s/',
  /** `/_/forms/{id}` and `/_/forms/{id}/submissions/{submissionId}`: the links of the forms e-mails (F-109). Need a session. */
  form: '/_/forms/',
  /** `/f/{publicToken}`: a form's public link, login optional (F-109). Short on purpose, like `/s/`. */
  publicForm: '/f/',
  /**
   * `/account-erasure/cancel?token=…`: the "Desistir" link of the account erasure e-mail (F-199), no login.
   * Outside `/_/` because the API writes it this way into e-mails already sent; the `/_/` spelling works too.
   */
  accountErasureCancel: '/account-erasure/cancel',
  /** `/privacy/exports/{exportId}`: the "your data is ready" e-mail link (F-200), the person whose data it is only. Outside `/_/` as the API writes it; `/_/privacy/exports/` works too. */
  privacyExport: '/privacy/exports/',
  /** `/_/exports/{exportId}`: the download of a big export, the link of the "your file is ready" e-mail (SEC-216). Requester only. */
  exportDownload: '/_/exports/',
  /**
   * `/_/subscriptions/change-plan/{plan}/{option}?addOns=…&back=…`: the plan change with the checkout's
   * layout (F-195). The plan and option parts are the plan's `slug` and the option's `optionKey`, as on
   * `/pricing`; `&company=…&subscription=…` is a company's subscription, changed by its admin. Needs a session.
   */
  changePlan: '/_/subscriptions/change-plan',
  /** `/_/account/emails?category={key}`: Minha Conta › E-mails desta comunidade, a category highlighted (F-104). Needs a session. */
  accountEmails: '/_/account/emails',
  /** Minha Conta › Faturamento: the subscription, cards and history (the `/billing` page until 2026-10-29). */
  accountBilling: '/account?tab=billing',
  /** Minha Conta › Segurança: password, two-step verification (F-233), leaving the community. The MCP links `/_/account/security`. */
  accountSecurity: '/account?tab=security',
  /** Minha Conta › Perfil: the profile editor (the "Editar perfil" dialog until 2026-10-29); `accountSectionPath` for the others. */
  accountProfile: '/account?tab=profile',
  /** `/_/email/unsubscribe/{token}`: one-click unsubscribe of one e-mail category, no login (F-104). */
  emailUnsubscribe: '/_/email/unsubscribe/',
  /** `/_/mcp/authorize?…`: the MCP consent screen (F-40). */
  mcpAuthorize: '/_/mcp/authorize',
  /** Resident companies (F-93): the Empresas tab of Membros; `&company={id}` is the detail. Owner, admin, finance and moderator. */
  companies: '/members?tab=companies',
  /** F-150: the requests to join (Pendentes tab of Membros). Owner and admin. The e-mail links `/_/members/pending`. */
  signupRequests: '/members?tab=pending',
  /** "My company" (F-93): the caller's company and team. */
  myCompany: '/my-company',
  /** SuperAdmin platform fees (F-43). */
  adminFees: '/admin/fees',
  /** SuperAdmin Communities section (F-65): the list; `/admin/communities/{id}` is the detail. */
  adminCommunities: '/admin/communities',
  /** SuperAdmin community requests (F-89): the Pedidos tab of Comunidades; `/admin/communities/requests/{id}` is the detail. */
  adminCommunityRequests: '/admin/communities/requests',
  /** SuperAdmin "Plataforma Memberfy" (F-34): settings of the platform itself (plan defaults). */
  adminPlatform: '/admin/platform',
  /** The item segment of a certificates space's management view. */
  certificatesManageSegment: 'manage'
};

/**
 * Paths that existed before and are still out there: QR codes on issued
 * certificates, e-mails already sent, MCP connectors, bookmarks. Each one is
 * answered with a permanent redirect to its current form.
 */
const LEGACY_PREFIXES = [
  ['/verify/', ROUTES.certificatesVerify],
  ['/pagamento/', ROUTES.checkoutPayment],
  ['/payment/', ROUTES.checkoutPayment],
  ['/oferta/', ROUTES.downsellOffer],
  ['/offer/', ROUTES.downsellOffer],
  /* Not legacy: the form the API's export e-mail uses (SEC-216). */
  ['/exports/', ROUTES.exportDownload],
  /* The requests were their own page in Administração until 2026-10-26; now the Pedidos tab of Comunidades. */
  ['/admin/community-requests', ROUTES.adminCommunityRequests]
];

const LEGACY_EXACT = {
  /* Not legacy: the form the API's company invitation e-mail uses (SEC-142); the query (`?invitation=`) is kept. */
  '/_/company/mine': ROUTES.myCompany,
  /* Not legacy: the link of the "new requests to join" e-mail and notice (F-150, the API's `APP_PATHS.signupRequests`). */
  '/_/members/pending': ROUTES.signupRequests,
  /* Not legacy: the API's `APP_PATHS.accountSecurity` (F-233, the MCP's "turn it on in the app" link). */
  '/_/account/security': ROUTES.accountSecurity,
  '/set-password': ROUTES.authSetPassword,
  '/oauth/authorize': ROUTES.mcpAuthorize,
  '/admin/taxas': ROUTES.adminFees,
  /* Faturamento was its own page until 2026-10-29; now the tab of Minha conta. The API's e-mails still link `/billing?subscription=…`. */
  '/billing': ROUTES.accountBilling
};

/**
 * Tabs that moved to another page, by `?tab=`: Gestão de assinaturas left
 * Monetização for Membros on 2026-10-06; Logs left Membros for its own page
 * in Administração on 2026-10-14 (`toTab: null` drops the tab, `rename`
 * carries the old filters over to their new names). The rest of the query is kept.
 */
const LEGACY_TABS = [
  /* Meus ingressos and Créditos left Minha conta for the menu on 2026-11-05. */
  { path: '/account', tab: 'tickets', to: ROUTES.myTickets, toTab: null },
  { path: '/account', tab: 'credits', to: ROUTES.myCredits, toTab: null },
  { path: '/monetization', tab: 'subscribers', to: '/members', toTab: 'subscriptions' },
  { path: '/members', tab: 'activity-logs', to: '/logs', toTab: null, rename: { source: 'type', profileId: 'actor' } },
  { path: '/members', tab: 'logs', to: '/logs', toTab: null, rename: { source: 'type', profileId: 'actor' } }
];

/**
 * Administração › Empresas (F-93) was its own page, `/companies` and `/companies/{id}`, until
 * 2026-10-16; it is now the Empresas tab of Membros, the detail under `&company={id}`.
 */
const LEGACY_COMPANIES = /^\/companies(?:\/([^/]+))?\/?$/;

/** A certificates management view used to end in `/gerenciar`. */
const LEGACY_MANAGE = /^(\/[^/]+\/[^/]+)\/gerenciar\/?$/;

/**
 * A section of Minha conta by its `?tab=` key: `profile`, `account`, `fields`, `page`, `privacy`,
 * `emails`, `security`, `billing`, `points`, `referral`, `appearance`, `assistants`, `apiKeys`.
 */
export const accountSectionPath = (section) => `/account?tab=${encodeURIComponent(section)}`;

/**
 * The current form of a path (a pathname, optionally with `?query#hash`);
 * the same string when nothing changed.
 */
export const canonicalPath = (path) => {
  const match = String(path).match(/^([^?#]*)(.*)$/);
  const pathname = match[1];
  const suffix = match[2];
  for (const [from, to] of LEGACY_PREFIXES) {
    if (pathname.startsWith(from)) return to + pathname.slice(from.length) + suffix;
  }
  const company = pathname.match(LEGACY_COMPANIES);
  if (company) {
    const [, search = '', hash = ''] = suffix.match(/^(?:\?([^#]*))?(#.*)?$/) || [];
    const params = new URLSearchParams(search);
    params.set('tab', 'companies');
    if (company[1]) params.set('company', decodeURIComponent(company[1]));
    return `/members?${params.toString()}${hash}`;
  }
  const exact = LEGACY_EXACT[pathname.replace(/\/+$/, '') || '/'];
  if (exact) {
    /* A target with its own query (`/account?tab=billing`) takes the old query after `&`. */
    if (exact.includes('?') && suffix.startsWith('?')) return `${exact}&${suffix.slice(1)}`;
    return exact + suffix;
  }
  const query = suffix.match(/^\?([^#]*)(#.*)?$/);
  if (query) {
    const params = new URLSearchParams(query[1]);
    const moved = LEGACY_TABS.find((x) => x.path === (pathname.replace(/\/+$/, '') || '/') && x.tab === params.get('tab'));
    if (moved) {
      if (moved.toTab) params.set('tab', moved.toTab);
      else params.delete('tab');
      for (const [from, to] of Object.entries(moved.rename || {})) {
        if (!params.has(from)) continue;
        params.set(to, params.get(from));
        params.delete(from);
      }
      const rest = params.toString();
      return `${moved.to}${rest ? `?${rest}` : ''}${query[2] || ''}`;
    }
  }
  const manage = pathname.match(LEGACY_MANAGE);
  if (manage) return `${manage[1]}/${ROUTES.certificatesManageSegment}${suffix}`;
  return path;
};
